<template>
    <div class="app-container mt-5">
        <div class="page-title mb-3">
            <router-link to="/configuration">
                <span class="breadcrumb-link">
                    <v-icon class="breadcrumb-link-icon">settings</v-icon> Configuration du compte
                </span>
            </router-link>
            <v-icon>keyboard_arrow_right</v-icon>
            <span class="breadcrumb-item">
                <v-icon class="breadcrumb-item-icon">contact_mail</v-icon> Mes modèles d'e-mails
            </span>
        </div>

        <div class="row">
            <div class="col-3">
                <v-alert text class="mb-5">
                    Les modèles d'e-mails permettent de définir un sujet et corps d'e-mail réutilisables lors de la
                    configuration de vos campagnes.
                </v-alert>
            </div>
            <div class="col">
                <v-card class="p-4">
                    <div class="d-flex">
                        <v-btn outlined @click="prepareCreate" class="mb-5 custom-button-primary">
                            <v-icon left>add</v-icon> Créer un modèle
                        </v-btn>
                    </div>

                    <div v-if="templates.length">
                        <v-data-table :headers="headers" :items="templates" :loading="$global.loading" class="border"
                            :footer-props="{
                                itemsPerPageAllText: 'Toutes',
                                itemsPerPageText: 'Modèles par page',
                                itemsPerPageOptions: [10, 20, 50, 100]
                            }">
                            <template v-slot:item.name="{ item }">
                                <a @click="prepareEdit(item)">{{ item.name }}</a>
                            </template>
                            <template v-slot:item.created_at="{ item }">
                                {{ formatDate(item.created_at, false) }}
                            </template>
                            <template v-slot:item.updated_at="{ item }">
                                {{ formatDate(item.updated_at, false) }}
                            </template>
                            <template v-slot:item.user="{ item }">
                                {{ item.user.first_name }} {{ item.user.last_name }}
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <v-btn small icon class="custom-button-error" @click="deleteTemplate(item)">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </div>

                    <div v-else class="text-center">
                        <small>Aucun modèle n'a été ajouté pour le moment</small>
                    </div>

                </v-card>
            </div>
        </div>

        <div class="custom-dialog" v-if="openTemplateDialog">
            <v-card>
                <v-card-title>
                    <div class="w-100 d-flex justify-content-between align-items-center">
                        <h3 v-if="!editMode">Création d'un modèle d'e-mail</h3>
                        <h3 v-else>Modification d'un modèle d'e-mail</h3>

                        <v-btn small icon v-on="on" class="custom-button-primary" @click="openTemplateDialog = false">
                            <v-icon>clear</v-icon>
                        </v-btn>
                    </div>

                </v-card-title>
                <v-card-text>
                    <h4><span class="required">Nom</span></h4>
                    <v-text-field background-color="white" outlined style="padding-top: 0!important;" hide-details
                        v-model="currentTemplate.name" class="subject-input mb-4">
                    </v-text-field>

                    <h4><span class="required">Objet</span></h4>
                    <v-text-field background-color="white" outlined style="padding-top: 0!important;" hide-details
                        v-model="currentTemplate.subject" class="subject-input mb-4">
                    </v-text-field>

                    <v-alert text class="mb-5" type="info">
                        Pour générer un objet en fonction du nom ou du type de prestataire, vous pouvez utiliser les
                        tags suivants :<br>
                        <b>#nom#</b> : le nom de la fiche<br>
                        <b>#type#</b> : le type de la fiche (ex: Restauration)
                    </v-alert>

                    <h4><span class="required">Première partie de l'e-mail</span></h4>
                    <ckeditor :editor="editor" v-model="currentTemplate.part1"></ckeditor>

                    <h4 class="mt-4">Lien du formulaire (généré automatiquement)</h4>
                    <div class="text-center valid-button mt-0">
                        <form-link :generated="true" name="Hôtel de France"></form-link>
                    </div>

                    <h4><span class="required">Dernière partie de l'e-mail</span></h4>
                    <ckeditor :editor="editor" v-model="currentTemplate.part2"></ckeditor>
                </v-card-text>

                <v-card-actions style="padding: 16px">
                    <v-spacer></v-spacer>
                    <div class="d-flex justify-content-end gap-10 w-100">
                        <v-btn @click="openTemplateDialog = false">Annuler</v-btn>
                        <v-btn class="default-button custom-button-primary" v-if="!editMode" @click="createTemplate"
                            :disabled="currentTemplate.part1 === null ||
                            currentTemplate.part2 === null ||
                            currentTemplate.subject === null ||
                            currentTemplate.name === null ||
                            currentTemplate.part1 === '' ||
                            currentTemplate.part2 === '' ||
                            currentTemplate.subject === '' ||
                            currentTemplate.name === ''">
                            Créer
                        </v-btn>
                        <v-btn class="default-button custom-button-primary" v-else @click="updateTemplate" :disabled="currentTemplate.part1 === null ||
                        currentTemplate.part2 === null ||
                        currentTemplate.subject === null ||
                        currentTemplate.name === null ||
                        currentTemplate.part1 === '' ||
                        currentTemplate.part2 === '' ||
                        currentTemplate.subject === '' ||
                        currentTemplate.name === ''">
                            Enregistrer
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </div>


        <confirm ref="confirm"></confirm>
    </div>
</template>

<script>
import ClassicEditor from "../../../plugins/ckeditor"
import FormLink from "../../campaign/cmaj/FormLink"
import Confirm from "../../tools/Confirm"

export default {
    name: "EmailTemplateConfig",
    components: { Confirm, FormLink },
    data() {
        return {
            editor: ClassicEditor,
            templates: [],
            openTemplateDialog: false,
            currentTemplate: {
                name: null,
                subject: null,
                part1: null,
                part2: null,
            },
            editMode: false,
            headers: [
                {
                    text: 'Nom',
                    value: 'name'
                },
                {
                    text: 'Créé le',
                    value: 'created_at'
                },
                {
                    text: 'Dernière modification',
                    value: 'updated_at'
                },
                {
                    text: 'Propriétaire',
                    value: 'user'
                },
                {
                    text: '',
                    value: 'actions'
                },
            ],
        }
    },
    methods: {
        getTemplates() {
            this.$global.loading = true

            this.$http.get('auth/account/email-templates').then(res => {
                this.templates = res.body.data.templates

                this.$global.loading = false
            })
        },
        createTemplate() {
            this.$global.loading = true

            let payload = {
                template: this.currentTemplate
            }

            this.$http.post('auth/account/email-templates', payload).then(res => {
                this.getTemplates()

                this.openTemplateDialog = false
                this.$global.loading = false

                this.currentTemplate = {
                    name: null,
                    subject: null,
                    part1: null,
                    part2: null,
                }
            })
        },
        prepareEdit(template) {
            this.editMode = true
            this.currentTemplate = JSON.parse(JSON.stringify(template))
            this.openTemplateDialog = true
        },
        deleteTemplate(template) {
            this.$refs.confirm.open('Attention',
                'Vous allez supprimer le modèle d\'e-mail <b>' + template.name,
                { color: 'red' }).
                then((confirm) => {
                    if (confirm) {
                        this.$global.loading = true

                        this.$http.delete('auth/account/email-templates/' + template.id).then(res => {
                            this.getTemplates()

                            this.$global.loading = false
                        })
                    }
                })
        },
        prepareCreate() {
            this.currentTemplate = {
                name: null,
                subject: null,
                part1: null,
                part2: null,
            }

            this.editMode = false
            this.openTemplateDialog = true
        },
        updateTemplate() {
            this.$global.loading = true

            let payload = {
                template: this.currentTemplate
            }

            this.$http.post('auth/account/email-templates/update', payload).then(res => {
                this.getTemplates()

                this.openTemplateDialog = false
                this.$global.loading = false

                this.currentTemplate = {
                    name: null,
                    subject: null,
                    part1: null,
                    part2: null,
                }
            })
        }
    },
    created() {
        this.getTemplates()
    }
}
</script>